import { z } from 'zod';

// OPS-SIGNAL — fixed, capped client ops-signal schema
// (docs/audit/fastfood-workflow/IMPLEMENTATION_ACTION_PLAN.md Phase 3 item 4).
//
// The client can observe two operational facts the server cannot: a dropped
// realtime channel and an abandoned counter ticket. Both are reported here and
// land in continuity_events under the ops:* family (see the sink route). Kept
// DB-free so the browser reporter can import the vocabulary and it can be
// unit-tested without the Supabase client — exactly the client-error-schema
// shape. `.strict()` rejects any field outside this set (doc 16 §8 — "never
// accepts arbitrary payloads").

export const MAX_ROUTE_LENGTH = 200;
export const MAX_CORRELATION_ID_LENGTH = 64;
export const MAX_BRANCH_ID_LENGTH = 64;
export const MAX_ITEM_COUNT = 1_000_000;

/** Pre-parse body guard: derived from the field caps, never a literal. */
export const MAX_BODY_BYTES =
  (MAX_ROUTE_LENGTH + MAX_CORRELATION_ID_LENGTH + MAX_BRANCH_ID_LENGTH + 96) * 2 + 256;

export const OP_SIGNAL_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The client-observable signals this endpoint accepts. Each maps to exactly one
 * `ops:*` continuity event in the sink route — this enum is the contract.
 */
export const OP_SIGNALS = ['realtime_disconnect', 'cart_abandoned'] as const;
export type OpSignal = (typeof OP_SIGNALS)[number];

export const opSignalSchema = z
  .object({
    signal: z.enum(OP_SIGNALS),
    route: z.string().min(1).max(MAX_ROUTE_LENGTH),
    correlation_id: z
      .string()
      .max(MAX_CORRELATION_ID_LENGTH)
      .regex(OP_SIGNAL_ID)
      .optional(),
    branch_id: z.string().max(MAX_BRANCH_ID_LENGTH).optional(),
    /** Source time of the event (client clock). Defaults to server now. */
    happened_at: z.string().datetime().optional(),
    /** Cart abandonment only: units on the ticket when it was dropped. */
    item_count: z.number().int().min(0).max(MAX_ITEM_COUNT).optional(),
  })
  .strict();

export type OpSignalInput = z.infer<typeof opSignalSchema>;
